"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import Image from "next/image";
import Link from "next/link";
import { SignInButton, SignUpButton } from "@clerk/nextjs";
import { useTranslations } from "@/lib/i18n-utils";
import { getFeatureCategories, getAiCategories, NavItem } from '@/lib/navigation';
import { useAuth } from '@clerk/nextjs';

export default function Navbar({ className, featureCategories, aiCategories, dark = false }: { className?: string; featureCategories?: { title: string; items: NavItem[] }[]; aiCategories?: { title: string; items: NavItem[] }[]; dark?: boolean }) {
  const { locale } = useTranslations();
  const { orgId } = useAuth();
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aiCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openFeatures = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (aiCloseTimerRef.current) clearTimeout(aiCloseTimerRef.current);
    setAiOpen(false);
    setFeaturesOpen(true);
  };

  const closeFeatures = () => {
    closeTimerRef.current = setTimeout(() => setFeaturesOpen(false), 500);
  };

  const openAi = () => {
    if (aiCloseTimerRef.current) clearTimeout(aiCloseTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setFeaturesOpen(false);
    setAiOpen(true);
  };

  const closeAi = () => {
    aiCloseTimerRef.current = setTimeout(() => setAiOpen(false), 500);
  };

  const getIcon = (name: string, idx = 0) => {
    const id = name.toLowerCase();
    const commonClass = 'h-4 w-4 text-green-700 flex-shrink-0';

    const fallbackIcons = [
      // circle
      (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ),
      // star
      (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M12 17.3L6.16 20l1.1-6.42L2 9.24l6.5-.95L12 2l3.5 6.29 6.5.95-5.26 4.34L17.84 20 12 17.3z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      // tag
      (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M20.59 13.41L13 5.82a2 2 0 0 0-2.83 0L3 13v6a2 2 0 0 0 2 2h6l7.59-7.59a2 2 0 0 0 0-2.83z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="7.5" cy="10.5" r="1" fill="currentColor" />
        </svg>
      ),
      // bolt
      (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      // shield
      (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M12 2l8 4v6c0 5-3.6 9.74-8 10-4.4-.26-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    ];

    if (id.includes('invoice') || id.includes('billing') || id.includes('payment') || id.includes('price')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 13h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 17h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (id.includes('time') || id.includes('timesheet') || id.includes('clock') || id.includes('hours')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 7v6l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (id.includes('calendar') || id.includes('date')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M16 3v4M8 3v4M3 11h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (id.includes('ai') || id.includes('automation') || id.includes('smart')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M12 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 18v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4.93 4.93l2.83 2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16.24 16.24l2.83 2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    }

    if (id.includes('document') || id.includes('doc') || id.includes('file') || id.includes('records')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (id.includes('job') || id.includes('jobs') || id.includes('task') || id.includes('project')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (id.includes('board') || id.includes('kanban') || id.includes('grid')) {
      return (
        <svg className={commonClass} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect x="3" y="3" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
          <rect x="13" y="3" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
          <rect x="3" y="13" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
          <rect x="13" y="13" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    }

    // return a varied fallback icon (based on index) instead of a checkmark
    return fallbackIcons[idx % fallbackIcons.length];
  };

  

  const renderedFeatureCategories = featureCategories && featureCategories.length ? featureCategories : getFeatureCategories(locale);

  useEffect(() => {
    // runtime debug: verify prop passed from pages
    // remove or silence in production
    // eslint-disable-next-line no-console
    console.log('Navbar: featureCategories prop ->', featureCategories);
    // eslint-disable-next-line no-console
    console.log('Navbar: renderedFeatureCategories ->', renderedFeatureCategories);
  }, [featureCategories, renderedFeatureCategories]);

  const linkClass = dark ? 'text-sm font-semibold text-slate-100 transition hover:text-emerald-300 inline-flex items-center gap-1 hover:bg-slate-800/20' : 'text-sm font-semibold text-slate-600 transition hover:text-green-700 inline-flex items-center gap-1 hover:bg-green-100/20';
  const simpleLinkClass = dark ? 'text-sm font-semibold text-slate-100 transition hover:text-emerald-300' : 'text-sm font-semibold text-slate-600 transition hover:text-green-700';
  const logoTextClass = dark ? 'text-2xl font-black tracking-tight text-white sm:text-3xl' : 'text-2xl font-black tracking-tight text-slate-900 sm:text-3xl';
  const navClasses = `fixed top-0 left-0 right-0 z-50 ${dark ? 'bg-black border-b border-slate-800/20 shadow-sm' : 'bg-white border-b border-slate-200 shadow-sm'} ${className ?? ''}`;
  const portalPanelClass = dark ? 'w-screen border-t border-slate-800 bg-slate-900 shadow-lg will-change-transform' : 'w-screen border-t border-slate-200 bg-white shadow-lg will-change-transform';

  // Portal wrapper for dropdown content to avoid stacking/transform issues
  const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [el] = useState<HTMLElement | null>(() => (typeof document !== 'undefined' ? document.createElement('div') : null));

    useEffect(() => {
      if (!el) return;
      document.body.appendChild(el);
      return () => {
        if (el.parentNode) el.parentNode.removeChild(el);
      };
    }, [el]);

    if (!el) return null;
    return createPortal(children, el);
  };

  return (
    <nav className={navClasses}>
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-2 px-4 py-3 md:py-4">
        <Link href="/" className="inline-flex items-center gap-3">
          <span className={`${logoTextClass} hidden sm:inline`}>Svendetid</span>
        </Link>
        <div className="hidden items-center gap-7 md:flex">
            <div className="relative group" onMouseEnter={openAi} onMouseLeave={closeAi}>
            <Link href="/ai" className={linkClass} aria-expanded={aiOpen}>
               {locale === "da" ? "SvendAI" : "SvendAI"}
            </Link>
          </div>

          <div className="relative group" onMouseEnter={openFeatures} onMouseLeave={closeFeatures}>
            <Link href={locale === "da" ? "/features" : "/en/features"} className={linkClass} aria-expanded={featuresOpen}>
              {locale === "da" ? "Funktioner" : "Features"}
              <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>

            {renderedFeatureCategories && (
              <Portal>
                <div
                  className={`${featuresOpen ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-y-0 -translate-y-2 pointer-events-none'} transform origin-top transition-all duration-200 ease-out fixed left-0 top-16 md:top-20 z-50 w-screen will-change-transform`}
                  onMouseEnter={openFeatures}
                  onMouseLeave={closeFeatures}
                >
                  <div className={portalPanelClass}>
                    <div className="mx-auto max-w-7xl px-4 py-6">
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">
                        {renderedFeatureCategories.map((cat) => (
                          <div key={cat.title} className="text-sm">
                            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-700">{cat.title}</h4>
                            <ul className="space-y-2">
                              {cat.items.map((navItem, idx) => (
                                <li key={navItem.label} className={`${dark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'} flex items-center gap-2 rounded-md px-2 py-1 transition-colors`}>
                                  {getIcon(navItem.label, idx)}
                                  {(() => {
                                    // Patch for Customer Database/Kundekartotek dynamic orgId
                                    const isCustomerFeature = navItem.href && navItem.href.includes('/organization/[organizationId]/customers/feature-page');
                                  if (isCustomerFeature) {
                                    if (orgId) {
                                      const href = navItem.href.replace('[organizationId]', orgId);
                                      return <Link href={href} className="transition">{navItem.label}</Link>;
                                    }
                                    // Fallback to a public marketing feature page when orgId is not available
                                    return <Link href="/features/customer-database" className="transition">{navItem.label}</Link>;
                                  }
                                    return navItem.href ? (
                                      <Link href={navItem.href} className="transition">{navItem.label}</Link>
                                    ) : (
                                      <a href="#features" className="transition">{navItem.label}</a>
                                    );
                                  })()}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Portal>
            )}
          </div>

          <Link href="/pricing" className={simpleLinkClass}>
            {locale === "da" ? "Priser" : "Pricing"}
          </Link>
          <Link href="#faq" className={simpleLinkClass}>
            {locale === "da" ? "FAQ" : "FAQ"}
          </Link>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3">

          <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
            <button type="button" className={dark ? 'min-w-[96px] text-center rounded-lg bg-white/10 px-3 py-1 text-sm font-semibold text-white hover:bg-white/20' : 'min-w-[96px] text-center rounded-lg bg-slate-200 px-3 py-1 text-sm font-semibold text-black hover:bg-slate-300'}>
              {locale === "da" ? "Log ind" : "Login"}
            </button>
          </SignInButton>

          <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
            <button type="button" className={dark ? 'min-w-[96px] text-center rounded-lg bg-emerald-500 text-black px-3 py-1 text-sm font-semibold hover:bg-emerald-400' : 'min-w-[96px] text-center rounded-lg bg-slate-900 text-white px-3 py-1 text-sm font-semibold hover:bg-slate-800'}>
              {locale === "da" ? "Tilmeld" : "Sign up"}
            </button>
          </SignUpButton>

          <button
            type="button"
            className={`inline-flex h-9 w-9 items-center justify-center rounded-lg md:hidden ${dark ? 'text-white hover:bg-white/10' : 'text-slate-900 hover:bg-slate-100'}`}
            aria-label={mobileOpen ? (locale === 'da' ? 'Luk menu' : 'Close menu') : (locale === 'da' ? 'Åbn menu' : 'Open menu')}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={`md:hidden border-t ${dark ? 'border-slate-800 bg-black' : 'border-slate-200 bg-white'}`}>
          <div className="mx-auto max-w-7xl px-4 py-3 space-y-1">
            <Link
              href="/ai"
              className={`block rounded-md px-2 py-2 text-sm font-semibold ${dark ? 'text-slate-100 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
              onClick={() => setMobileOpen(false)}
            >
              SvendAI
            </Link>

            <button
              type="button"
              className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-sm font-semibold ${dark ? 'text-slate-100 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
              aria-expanded={mobileFeaturesOpen}
              onClick={() => setMobileFeaturesOpen((open) => !open)}
            >
              {locale === 'da' ? 'Funktioner' : 'Features'}
              <svg className={`h-4 w-4 transition-transform ${mobileFeaturesOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {mobileFeaturesOpen && (
              <div className="space-y-3 py-2 pl-4">
                {renderedFeatureCategories.map((cat) => (
                  <div key={cat.title}>
                    <h4 className={`mb-1 text-xs font-bold uppercase tracking-wide ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{cat.title}</h4>
                    <ul className="space-y-1">
                      {cat.items.map((navItem, idx) => {
                        const isCustomerFeature = navItem.href && navItem.href.includes('/organization/[organizationId]/customers/feature-page');
                        let href = navItem.href;
                        if (isCustomerFeature) {
                          href = orgId ? navItem.href!.replace('[organizationId]', orgId) : '/features/customer-database';
                        }
                        return (
                          <li key={navItem.label} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${dark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                            {getIcon(navItem.label, idx)}
                            {href ? (
                              <Link href={href} className="transition" onClick={() => setMobileOpen(false)}>{navItem.label}</Link>
                            ) : (
                              <a href="#features" className="transition" onClick={() => setMobileOpen(false)}>{navItem.label}</a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <Link
              href="/pricing"
              className={`block rounded-md px-2 py-2 text-sm font-semibold ${dark ? 'text-slate-100 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
              onClick={() => setMobileOpen(false)}
            >
              {locale === 'da' ? 'Priser' : 'Pricing'}
            </Link>

            <Link
              href="#faq"
              className={`block rounded-md px-2 py-2 text-sm font-semibold ${dark ? 'text-slate-100 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
              onClick={() => setMobileOpen(false)}
            >
              FAQ
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

